/**
 * Reusable Schedule Calendar Component
 * Shows appointments on a monthly calendar view
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  addMonths,
  subMonths,
  getDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, Clock, User, XCircle, Loader2, UserPlus, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Status-based colors (takes priority over type colors)
const statusColors = {
  completed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
  missed: "bg-yellow-100 text-yellow-800 border-yellow-400",
  missed_punch: "bg-orange-100 text-orange-700 border-orange-300",
  no_show: "bg-red-200 text-red-800 border-red-400",
  scheduled: "bg-blue-100 text-blue-700 border-blue-200",
  in_progress: "bg-cyan-100 text-cyan-700 border-cyan-200",
  open: "bg-purple-100 text-purple-700 border-purple-300",
};

const appointmentTypeColors = {
  visit: "bg-slate-100 text-slate-700 border-slate-200",
  follow_up: "bg-purple-100 text-purple-700 border-purple-200",
  assessment: "bg-blue-100 text-blue-700 border-blue-200",
  supervisory: "bg-rose-100 text-rose-700 border-rose-200",
  md_appointment: "bg-amber-100 text-amber-700 border-amber-200",
  therapy: "bg-pink-100 text-pink-700 border-pink-200",
  open: "bg-orange-100 text-orange-700 border-orange-200",
};

export function ScheduleCalendar({ 
  appointments = [], 
  loading = false,
  showClientName = true,
  showEmployeeName = false,
  onDateClick,
  selectedDate: externalSelectedDate,
  onAppointmentCancelled,
  allowCancel = true,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(externalSelectedDate || null);
  
  // Cancel shift dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [notifyEmployee, setNotifyEmployee] = useState(true);
  const [createOpenShift, setCreateOpenShift] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  
  // Assign caregiver dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [appointmentToAssign, setAppointmentToAssign] = useState(null);
  const [availableCaregivers, setAvailableCaregivers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState("");
  const [loadingCaregivers, setLoadingCaregivers] = useState(false);
  const [assigning, setAssigning] = useState(false);
  
  // Replacement caregiver in cancel dialog
  const [replacementCaregiver, setReplacementCaregiver] = useState("");
  const [replacementCaregivers, setReplacementCaregivers] = useState([]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const firstDayOfMonth = getDay(monthStart);

  // Helper function to calculate end time from start time and duration
  const getTimeRange = (startTime, durationMinutes) => {
    if (!startTime) return '';
    const start = startTime.slice(0, 5); // "HH:MM"
    if (!durationMinutes) return start;
    
    const [hours, mins] = start.split(':').map(Number);
    const totalMins = hours * 60 + mins + durationMinutes;
    const endHours = Math.floor(totalMins / 60) % 24;
    const endMins = totalMins % 60;
    const end = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
    
    // Return compact format: "1100-1200"
    return `${start.replace(':', '')}-${end.replace(':', '')}`;
  };

  // Helper function to get color based on appointment status (priority) then type
  const getAppointmentColor = (appt) => {
    // Check for needs coverage first (open shifts)
    if (appt.status === 'open' || appt.needs_coverage) {
      return statusColors.open;
    }
    // Status-based colors take priority
    if (appt.status && statusColors[appt.status]) {
      return statusColors[appt.status];
    }
    // Check for missed punch (if has clock_in_time but no clock_out_time for past appointments)
    if (appt.status === 'missed_punch' || appt.missed_punch) {
      return statusColors.missed_punch;
    }
    // Fall back to type-based colors
    if (appt.is_assessment) {
      return "bg-blue-100 text-blue-700 border-blue-200";
    }
    return appointmentTypeColors[appt.appointment_type] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  // Helper to get status indicator emoji/icon
  const getStatusIndicator = (appt) => {
    if (appt.status === 'open' || appt.needs_coverage) return '⚠️';
    if (appt.status === 'completed') return '✓';
    if (appt.status === 'cancelled') return '✗';
    if (appt.status === 'missed' || appt.status === 'no_show') return '!';
    if (appt.status === 'missed_punch' || appt.missed_punch) return '⏰';
    return '';
  };

  const getAppointmentsForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return appointments.filter((appt) => appt.scheduled_date === dateStr);
  };

  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateClick = (day) => {
    setSelectedDate(day);
    if (onDateClick) {
      onDateClick(day);
    }
  };

  // Fetch available caregivers for a specific appointment
  const fetchAvailableCaregivers = async (appt) => {
    setLoadingCaregivers(true);
    try {
      const response = await axios.get(
        `${API}/appointments/available-caregivers`,
        {
          params: {
            date: appt.scheduled_date,
            time: appt.scheduled_time,
            duration_minutes: appt.duration_minutes || 60
          }
        }
      );
      return response.data.available_caregivers || [];
    } catch (error) {
      console.error("Failed to fetch available caregivers:", error);
      return [];
    } finally {
      setLoadingCaregivers(false);
    }
  };

  // Cancel shift handlers
  const openCancelDialog = async (appt) => {
    setAppointmentToCancel(appt);
    setCancelReason("");
    setNotifyEmployee(true);
    setCreateOpenShift(true);
    setReplacementCaregiver("");
    setCancelDialogOpen(true);
    
    // Fetch available caregivers for replacement
    const caregivers = await fetchAvailableCaregivers(appt);
    setReplacementCaregivers(caregivers);
  };

  const handleCancelShift = async () => {
    if (!appointmentToCancel || !cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }

    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/appointments/${appointmentToCancel.id}/cancel`,
        {
          reason: cancelReason.trim(),
          notify_employee: notifyEmployee,
          create_open_shift: createOpenShift,
          replacement_employee_id: (replacementCaregiver && replacementCaregiver !== 'no-replacement') ? replacementCaregiver : null
        }
      );

      toast.success("Shift cancelled successfully");
      
      if (response.data.open_shift_created) {
        if (response.data.open_shift_assigned) {
          toast.success("Replacement caregiver assigned to new shift");
        } else {
          toast.info("Open shift created - needs coverage");
        }
      }
      
      if (response.data.notifications_sent) {
        const sent = response.data.notifications_sent;
        const notificationTypes = [];
        if (sent.in_app) notificationTypes.push("in-app");
        if (sent.email) notificationTypes.push("email");
        if (sent.sms) notificationTypes.push("SMS");
        
        if (notificationTypes.length > 0) {
          toast.info(`Employee notified via: ${notificationTypes.join(", ")}`);
        }
      }

      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setCancelReason("");
      setReplacementCaregiver("");
      
      // Callback to refresh appointments
      if (onAppointmentCancelled) {
        onAppointmentCancelled(appointmentToCancel.id);
      }
    } catch (error) {
      console.error("Failed to cancel shift:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel shift");
    } finally {
      setCancelling(false);
    }
  };

  // Assign caregiver handlers
  const openAssignDialog = async (appt) => {
    setAppointmentToAssign(appt);
    setSelectedCaregiver("");
    setAssignDialogOpen(true);
    
    // Fetch available caregivers
    const caregivers = await fetchAvailableCaregivers(appt);
    setAvailableCaregivers(caregivers);
  };

  const handleAssignCaregiver = async () => {
    if (!appointmentToAssign || !selectedCaregiver) {
      toast.error("Please select a caregiver");
      return;
    }

    setAssigning(true);
    try {
      await axios.post(
        `${API}/appointments/${appointmentToAssign.id}/assign-caregiver`,
        { employee_id: selectedCaregiver }
      );

      toast.success("Caregiver assigned successfully");
      
      setAssignDialogOpen(false);
      setAppointmentToAssign(null);
      setSelectedCaregiver("");
      
      // Callback to refresh appointments
      if (onAppointmentCancelled) {
        onAppointmentCancelled(appointmentToAssign.id);
      }
    } catch (error) {
      console.error("Failed to assign caregiver:", error);
      toast.error(error.response?.data?.detail || "Failed to assign caregiver");
    } finally {
      setAssigning(false);
    }
  };

  // Get selected day's appointments
  const selectedDayAppointments = selectedDate 
    ? getAppointmentsForDay(selectedDate) 
    : [];

  return (
    <div className="space-y-4" data-testid="schedule-calendar">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-slate-800">
          {format(currentDate, "MMMM yyyy")}
        </h4>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevMonth}
            data-testid="cal-prev-month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextMonth}
            data-testid="cal-next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Calendar className="w-8 h-8 animate-pulse text-slate-400" />
        </div>
      ) : (
        <>
          {/* Color Legend */}
          <div className="flex flex-wrap gap-3 mb-4 p-2 bg-slate-50 rounded-lg text-xs">
            <span className="font-medium text-slate-600">Legend:</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></span>
              Scheduled
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>
              ✓ Completed
            </span>
            <span className="flex items-center gap-1" data-testid="legend-supervisory">
              <span className="w-3 h-3 rounded bg-rose-100 border border-rose-200"></span>
              RN Supervisory
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span>
              ✗ Cancelled
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-400"></span>
              ! Missed/No-Show
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-orange-100 border border-orange-300"></span>
              ⏰ Missed Punch
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span>
              ⚠️ Open Shift
            </span>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-slate-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before the first of the month */}
            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
              <div
                key={`empty-${index}`}
                className="h-20 bg-slate-50 rounded-lg"
              />
            ))}

            {/* Days of the month */}
            {days.map((day) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = isSameDay(day, new Date());
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const hasAppointments = dayAppointments.length > 0;
              const hasOpenShifts = dayAppointments.some(a => a.status === 'open' || a.needs_coverage);

              return (
                <div
                  key={day.toISOString()}
                  className={`h-20 rounded-lg border p-1 cursor-pointer transition-colors ${
                    isToday
                      ? "border-primary bg-primary/5"
                      : isSelected
                      ? "border-primary/50 bg-primary/10"
                      : hasOpenShifts
                      ? "border-orange-300 bg-orange-50/50 hover:border-orange-400"
                      : hasAppointments
                      ? "border-emerald-200 bg-emerald-50/50 hover:border-emerald-300"
                      : "border-slate-100 hover:border-slate-300"
                  }`}
                  onClick={() => handleDateClick(day)}
                  data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-medium ${
                        isToday ? "text-primary" : "text-slate-700"
                      }`}
                    >
                      {format(day, "d")}
                    </span>
                    {hasAppointments && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        hasOpenShifts ? "bg-orange-500 text-white" : "bg-primary text-white"
                      }`}>
                        {dayAppointments.length}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5 overflow-hidden mt-1">
                    {dayAppointments.slice(0, 2).map((appt) => (
                      <div
                        key={appt.id}
                        className={`text-xs px-1 py-0.5 rounded truncate border ${getAppointmentColor(appt)}`}
                        title={`${getTimeRange(appt.scheduled_time, appt.duration_minutes)} - ${appt.status === 'open' || appt.needs_coverage ? 'Needs Coverage' : (showClientName ? appt.client_name : appt.employee_name)} (${appt.status || 'scheduled'})`}
                      >
                        {getStatusIndicator(appt)}{getTimeRange(appt.scheduled_time, appt.duration_minutes)}
                      </div>
                    ))}
                    {dayAppointments.length > 2 && (
                      <div className="text-xs text-slate-500 px-1">
                        +{dayAppointments.length - 2}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected Day Details */}
          {selectedDate && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h5 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
                <span className="text-sm font-normal text-slate-500">
                  ({selectedDayAppointments.length} appointment{selectedDayAppointments.length !== 1 ? 's' : ''})
                </span>
              </h5>
              
              {selectedDayAppointments.length === 0 ? (
                <p className="text-sm text-slate-500">No appointments scheduled for this day</p>
              ) : (
                <div className="space-y-2">
                  {selectedDayAppointments
                    .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''))
                    .map((appt) => (
                    <div
                      key={appt.id}
                      className={`p-3 rounded-lg border ${
                        appt.status === 'open' || appt.needs_coverage
                          ? 'bg-orange-50 border-orange-200'
                          : appt.status === 'completed'
                          ? 'bg-emerald-50 border-emerald-200'
                          : appt.status === 'cancelled'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-800">
                              {getTimeRange(appt.scheduled_time, appt.duration_minutes)}
                            </span>
                            <span className="text-slate-500">
                              ({Math.floor((appt.duration_minutes || 60) / 60)}h {(appt.duration_minutes || 60) % 60 > 0 ? `${(appt.duration_minutes || 60) % 60}m` : ''})
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              appt.status === 'open' || appt.needs_coverage
                                ? 'bg-purple-100 text-purple-700'
                                : appt.status === 'scheduled'
                                ? 'bg-blue-100 text-blue-700'
                                : appt.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : appt.status === 'cancelled'
                                ? 'bg-red-100 text-red-700'
                                : appt.status === 'missed' || appt.status === 'no_show'
                                ? 'bg-yellow-100 text-yellow-800'
                                : appt.status === 'missed_punch'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {appt.status === 'open' || appt.needs_coverage 
                                ? 'Open Shift' 
                                : appt.status === 'missed_punch'
                                ? 'Missed Punch'
                                : appt.status === 'no_show'
                                ? 'No Show'
                                : appt.status?.charAt(0).toUpperCase() + appt.status?.slice(1)}
                            </span>
                            {(appt.status === 'open' || appt.needs_coverage) && (
                              <span className="flex items-center gap-1 text-purple-600 text-xs">
                                <AlertCircle className="w-3 h-3" />
                                Needs Coverage
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm">
                            {showClientName && appt.client_name && (
                              <span className="flex items-center gap-1 text-slate-700">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">Client:</span> {appt.client_name}
                              </span>
                            )}
                            {showEmployeeName && appt.employee_name && (
                              <span className="flex items-center gap-1 text-slate-700">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">Caregiver:</span> {appt.employee_name}
                              </span>
                            )}
                            {(appt.status === 'open' || appt.needs_coverage) && appt.original_employee_name && (
                              <span className="flex items-center gap-1 text-slate-500 text-xs">
                                (Originally: {appt.original_employee_name})
                              </span>
                            )}
                          </div>
                          
                          {appt.notes && (
                            <p className="text-xs text-slate-500 mt-2 italic">
                              {appt.notes}
                            </p>
                          )}
                          
                          {/* Show cancellation reason if cancelled */}
                          {appt.status === 'cancelled' && appt.cancellation_reason && (
                            <p className="text-xs text-red-600 mt-2">
                              <span className="font-medium">Cancellation reason:</span> {appt.cancellation_reason}
                            </p>
                          )}
                          
                          {/* Show coverage notes for open shifts */}
                          {(appt.status === 'open' || appt.needs_coverage) && appt.coverage_notes && (
                            <p className="text-xs text-orange-600 mt-2">
                              <span className="font-medium">Note:</span> {appt.coverage_notes}
                            </p>
                          )}
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex flex-col gap-2">
                          {/* Assign Caregiver button - for open shifts */}
                          {(appt.status === 'open' || appt.needs_coverage) && (
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-orange-600 hover:bg-orange-700"
                              onClick={() => openAssignDialog(appt)}
                              data-testid={`assign-caregiver-${appt.id}`}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Assign Caregiver
                            </Button>
                          )}
                          
                          {/* Cancel button - only show for scheduled appointments */}
                          {allowCancel && appt.status === 'scheduled' && !appt.is_assessment && !appt.needs_coverage && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openCancelDialog(appt)}
                              data-testid={`cancel-shift-${appt.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancel Shift
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Cancel Shift Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancel Shift
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this shift? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {appointmentToCancel && (
            <div className="space-y-4">
              {/* Shift details */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-sm space-y-1">
                  <p><span className="font-medium">Date:</span> {appointmentToCancel.scheduled_date}</p>
                  <p><span className="font-medium">Time:</span> {appointmentToCancel.scheduled_time}</p>
                  {appointmentToCancel.client_name && (
                    <p><span className="font-medium">Client:</span> {appointmentToCancel.client_name}</p>
                  )}
                  {appointmentToCancel.employee_name && (
                    <p><span className="font-medium">Caregiver:</span> {appointmentToCancel.employee_name}</p>
                  )}
                </div>
              </div>
              
              {/* Cancellation reason */}
              <div className="space-y-2">
                <Label htmlFor="cancel-reason" className="text-sm font-medium">
                  Cancellation Reason <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="cancel-reason"
                  placeholder="Please provide a reason for cancelling this shift..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  data-testid="cancel-reason-input"
                />
              </div>
              
              {/* Create open shift checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="create-open-shift"
                  checked={createOpenShift}
                  onCheckedChange={setCreateOpenShift}
                  data-testid="create-open-shift-checkbox"
                />
                <label
                  htmlFor="create-open-shift"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Create open shift (needs coverage)
                </label>
              </div>
              
              {/* Replacement caregiver dropdown */}
              {createOpenShift && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Assign Replacement Caregiver (optional)
                  </Label>
                  <Select value={replacementCaregiver} onValueChange={setReplacementCaregiver}>
                    <SelectTrigger data-testid="replacement-caregiver-select">
                      <SelectValue placeholder="Select available caregiver..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-replacement">No replacement - leave as open shift</SelectItem>
                      {loadingCaregivers ? (
                        <SelectItem value="loading" disabled>Loading caregivers...</SelectItem>
                      ) : replacementCaregivers.length === 0 ? (
                        <SelectItem value="none" disabled>No caregivers available</SelectItem>
                      ) : (
                        replacementCaregivers.map((cg) => (
                          <SelectItem key={cg.id} value={cg.id}>
                            {cg.name} {cg.role ? `(${cg.role})` : ''}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Only showing caregivers available on {appointmentToCancel.scheduled_date} at {appointmentToCancel.scheduled_time}
                  </p>
                </div>
              )}
              
              {/* Notify employee checkbox */}
              {appointmentToCancel.employee_id && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notify-employee"
                    checked={notifyEmployee}
                    onCheckedChange={setNotifyEmployee}
                    data-testid="notify-employee-checkbox"
                  />
                  <label
                    htmlFor="notify-employee"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Notify original employee (in-app, email, and SMS)
                  </label>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
            >
              Keep Shift
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelShift}
              disabled={cancelling || !cancelReason.trim()}
              data-testid="confirm-cancel-shift-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel Shift"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Caregiver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <UserPlus className="w-5 h-5" />
              Assign Caregiver
            </DialogTitle>
            <DialogDescription>
              Select an available caregiver to cover this shift.
            </DialogDescription>
          </DialogHeader>
          
          {appointmentToAssign && (
            <div className="space-y-4">
              {/* Shift details */}
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm space-y-1">
                  <p><span className="font-medium">Date:</span> {appointmentToAssign.scheduled_date}</p>
                  <p><span className="font-medium">Time:</span> {appointmentToAssign.scheduled_time}</p>
                  <p><span className="font-medium">Duration:</span> {appointmentToAssign.duration_minutes || 60} minutes</p>
                  {appointmentToAssign.client_name && (
                    <p><span className="font-medium">Client:</span> {appointmentToAssign.client_name}</p>
                  )}
                  {appointmentToAssign.original_employee_name && (
                    <p className="text-orange-600">
                      <span className="font-medium">Originally assigned to:</span> {appointmentToAssign.original_employee_name}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Caregiver selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Select Available Caregiver <span className="text-red-500">*</span>
                </Label>
                <Select value={selectedCaregiver} onValueChange={setSelectedCaregiver}>
                  <SelectTrigger data-testid="assign-caregiver-select">
                    <SelectValue placeholder="Select a caregiver..." />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingCaregivers ? (
                      <SelectItem value="loading" disabled>Loading caregivers...</SelectItem>
                    ) : availableCaregivers.length === 0 ? (
                      <SelectItem value="none" disabled>No caregivers available for this time</SelectItem>
                    ) : (
                      availableCaregivers.map((cg) => (
                        <SelectItem key={cg.id} value={cg.id}>
                          {cg.name} {cg.role ? `(${cg.role})` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Only showing caregivers available on {appointmentToAssign.scheduled_date} at {appointmentToAssign.scheduled_time}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setAssignDialogOpen(false)}
              disabled={assigning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignCaregiver}
              disabled={assigning || !selectedCaregiver}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-assign-caregiver-btn"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Caregiver
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
